import React from 'react';
import styled from 'styled-components';
import { formatDateToLong } from '../../utils/dateUtils';

// Styled Components
const FormSection = styled.section`
  margin-bottom: 2rem;
  
  h3 {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #f8f9fa;
    color: var(--primary);
  }
`;

const SummaryCard = styled.div`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
`;

const SummarySection = styled.div`
  margin-bottom: 1.5rem;
  
  h4 {
    color: var(--primary);
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e9ecef;
  }
  
  .summary-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
    
    @media (min-width: 768px) {
      grid-template-columns: 1fr 1fr;
    }
  }
`;

const SummaryItem = styled.div`
  .label {
    font-size: 0.85rem;
    color: var(--gray);
    margin-bottom: 0.2rem;
    font-weight: 500;
  }
  
  .value {
    font-weight: 500;
    color: var(--dark);
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: var(--primary);
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  margin-top: 1rem;
  display: inline-flex;
  align-items: center;
  
  &:hover {
    color: var(--primary-dark);
  }
  
  i {
    margin-right: 0.25rem;
  }
`;

const DisclaimerBox = styled.div`
  background-color: #f8f9fa;
  border-left: 4px solid var(--secondary);
  padding: 1rem;
  margin-top: 2rem;
  border-radius: 4px;
  font-size: 0.9rem;
  color: var(--gray);
`;

const SummaryComponent = ({ formData, goToStep }) => {
  return (
    <FormSection>
      <h3>Baby's Profile Summary</h3>
      <p className="text-muted mb-4">Please review the information you've provided. You can edit any section before generating your personalized sleep plan.</p>
      
      <SummaryCard>
        <SummarySection>
          <h4>Your Baby's Sleep Story</h4>
          <div className="summary-grid">
            <SummaryItem>
              <div className="label">Name</div>
              <div className="value">{formData.babyName || 'Not provided'}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Gender</div>
              <div className="value">{formData.gender || 'Not provided'}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Date of Birth</div>
              <div className="value">{formatDateToLong(formData.birthDate)}</div>
            </SummaryItem>
            {formData.isPremature && (
              <SummaryItem>
                <div className="label">Corrected Age</div>
                <div className="value">{formData.correctedAge}</div>
              </SummaryItem>
            )}
            <SummaryItem>
              <div className="label">Typical Bedtime</div>
              <div className="value">{formData.bedtime}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Typical Wake-up Time</div>
              <div className="value">{formData.wakeupTime}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Number of Naps</div>
              <div className="value">{formData.numberOfNaps}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Nap Durations</div>
              <div className="value">{formData.napDurations}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Sleep Associations</div>
              <div className="value">
                {Object.entries(formData.sleepAssociations)
                  .filter(([_, value]) => value)
                  .map(([key, _]) => {
                    const labels = {
                      rocked: 'Rocked',
                      dummy: 'Dummy/Pacifier',
                      fedToSleep: 'Fed to sleep',
                      independentlySleeps: 'Independently falls asleep'
                    };
                    return labels[key];
                  })
                  .join(', ') || 'None selected'}
              </div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Self-Settles</div>
              <div className="value">{formData.selfSettles}</div>
            </SummaryItem>
          </div>
          <EditButton onClick={() => goToStep(1)}>Edit this section</EditButton>
        </SummarySection>
        
        <SummarySection>
          <h4>Feeding and Night Waking Patterns</h4>
          <div className="summary-grid">
            <SummaryItem>
              <div className="label">Feeding Type</div>
              <div className="value">{formData.feedingType}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Overnight Feeds</div>
              <div className="value">{formData.overnightFeeds}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Falls Asleep During Feeds</div>
              <div className="value">{formData.fallsAsleepDuringFeeds}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Wakes Multiple Times</div>
              <div className="value">{formData.wakesMultipleTimes}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Response to Wakes</div>
              <div className="value">
                {Object.entries(formData.responseToWakes)
                  .filter(([key, value]) => value && key !== 'other')
                  .map(([key, _]) => {
                    const labels = {
                      feed: 'Feed',
                      rock: 'Rock',
                      dummy: 'Dummy',
                      cuddle: 'Cuddle',
                      wait: 'Wait'
                    };
                    return labels[key];
                  })
                  .concat(formData.responseToWakes.other ? [formData.responseToWakesOther || 'Other'] : [])
                  .join(', ') || 'None selected'}
              </div>
            </SummaryItem>
          </div>
          <EditButton onClick={() => goToStep(2)}>Edit this section</EditButton>
        </SummarySection>
        
        <SummarySection>
          <h4>Sleep Environment and Parenting Preferences</h4>
          <div className="summary-grid">
            <SummaryItem>
              <div className="label">Sleep Location</div>
              <div className="value">{formData.sleepLocation}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Dark Room</div>
              <div className="value">{formData.isDarkRoom}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">White Noise</div>
              <div className="value">{formData.usesWhiteNoise}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Sleepwear</div>
              <div className="value">{formData.sleepwear}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Room Temperature</div>
              <div className="value">{formData.roomTemperature || 'Not provided'}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Room Sharing</div>
              <div className="value">{formData.roomSharing}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Parenting Style</div>
              <div className="value">{formData.parentingStylePreference}</div>
            </SummaryItem>
            <SummaryItem>
              <div className="label">Main Goals</div>
              <div className="value">
                {Object.entries(formData.mainGoals)
                  .filter(([_, value]) => value)
                  .map(([key, _]) => {
                    const labels = {
                      longerStretches: 'Longer stretches',
                      selfSettling: 'Self-settling',
                      betterNaps: 'Better naps',
                      reducingNightWakings: 'Reducing night wakings'
                    };
                    return labels[key];
                  })
                  .join(', ') || 'None selected'}
              </div>
            </SummaryItem>
          </div>
          {formData.extraNotes && (
            <SummaryItem className="mt-3">
              <div className="label">Extra Notes</div>
              <div className="value">{formData.extraNotes}</div>
            </SummaryItem>
          )}
          <EditButton onClick={() => goToStep(3)}>Edit this section</EditButton>
        </SummarySection>
      </SummaryCard>
      
      <DisclaimerBox>
        <p>
          <strong>Disclaimer:</strong> Dozzy provides guidance based on trusted sources (Triple P, Red Nose Australia, Raising Children Network, Tresillian). This is not medical advice. Please consult a healthcare professional for medical concerns.
        </p>
      </DisclaimerBox>
    </FormSection>
  );
};

export default SummaryComponent;
